import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveKnowledgePage,
  createKnowledgePage,
  createKnowledgeTag,
  createThought,
  deleteKnowledgePage,
  deleteKnowledgeTag,
  deleteThought,
  getKnowledgePage,
  listKnowledgeTags,
  listKnowledgeTree,
  listPageVersions,
  listThoughts,
  moveKnowledgePage,
  revertPage,
  searchThoughts,
  setKnowledgePageTags,
  updateKnowledgePage
} from '../api/knowledge';

const keys = {
  tree: ['knowledge', 'tree'] as const,
  page: (id: string) => ['knowledge', 'page', id] as const,
  versions: (id: string) => ['knowledge', 'versions', id] as const,
  thoughts: ['knowledge', 'thoughts'] as const,
  thoughtSearch: (q: string) => ['knowledge', 'thoughts', 'search', q] as const,
  tags: ['knowledge', 'tags'] as const
};

export function useKnowledgeTree() {
  return useQuery({ queryKey: keys.tree, queryFn: () => listKnowledgeTree(), staleTime: 15_000 });
}

export function useKnowledgePage(id: string | null) {
  return useQuery({
    queryKey: keys.page(id ?? 'none'),
    queryFn: () => getKnowledgePage(id as string),
    enabled: Boolean(id)
  });
}

export function useCreatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createKnowledgePage,
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree })
  });
}

export function useUpdatePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) =>
      updateKnowledgePage(id, patch),
    onSuccess: (dto) => {
      qc.setQueryData(keys.page(dto.id), dto);
      qc.invalidateQueries({ queryKey: keys.tree });
      qc.invalidateQueries({ queryKey: keys.versions(dto.id) });
    }
  });
}

export function useMovePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      parentId,
      paraCategory
    }: {
      id: string;
      parentId?: string | null;
      paraCategory?: string;
    }) => moveKnowledgePage(id, { parentId, paraCategory }),
    // A move re-categorises the whole subtree server-side, so refetch the tree.
    onSuccess: (dto) => {
      qc.setQueryData(keys.page(dto.id), dto);
      qc.invalidateQueries({ queryKey: keys.tree });
    }
  });
}

export function useArchivePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => archiveKnowledgePage(id),
    onSuccess: (dto) => {
      qc.setQueryData(keys.page(dto.id), dto);
      qc.invalidateQueries({ queryKey: keys.tree });
    }
  });
}

export function useDeletePage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteKnowledgePage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tree })
  });
}

export function useSetPageTags() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, tagIds }: { id: string; tagIds: string[] }) =>
      setKnowledgePageTags(id, tagIds),
    onSuccess: (dto) => {
      qc.setQueryData(keys.page(dto.id), dto);
      qc.invalidateQueries({ queryKey: keys.tree });
    }
  });
}

export function usePageVersions(id: string | null) {
  return useQuery({
    queryKey: keys.versions(id ?? 'none'),
    queryFn: () => listPageVersions(id as string),
    enabled: Boolean(id)
  });
}

export function useRevertPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, versionNo }: { id: string; versionNo: number }) => revertPage(id, versionNo),
    onSuccess: (dto) => {
      qc.setQueryData(keys.page(dto.id), dto);
      qc.invalidateQueries({ queryKey: keys.versions(dto.id) });
      qc.invalidateQueries({ queryKey: keys.tree });
    }
  });
}

export function useThoughts() {
  return useQuery({ queryKey: keys.thoughts, queryFn: () => listThoughts(), staleTime: 10_000 });
}

export function useThoughtSearch(q: string) {
  return useQuery({
    queryKey: keys.thoughtSearch(q),
    queryFn: () => searchThoughts(q),
    enabled: q.trim().length > 0
  });
}

export function useCreateThought() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, source }: { body: string; source?: string }) => createThought(body, source),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.thoughts })
  });
}

export function useDeleteThought() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteThought(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.thoughts })
  });
}

export function useKnowledgeTags() {
  return useQuery({ queryKey: keys.tags, queryFn: listKnowledgeTags, staleTime: 30_000 });
}

export function useCreateKnowledgeTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, color }: { name: string; color?: string }) => createKnowledgeTag(name, color),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tags })
  });
}

export function useDeleteKnowledgeTag() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteKnowledgeTag(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.tags })
  });
}
